"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Like `useInViewOnce`, but it keeps observing: the flag goes true every time
 * the element arrives and false once it has left completely, for entrances
 * that should play again on the way back up.
 *
 * The boolean form exists for the same reason it does there — a class added
 * imperatively is lost the next time React rewrites the attribute, so
 * anything whose `className` React also computes has to take the flag through
 * a render.
 */
export function useInView<T extends HTMLElement>(threshold = 0.4) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      // Next frame rather than inline: setting state straight from an effect
      // body is a cascading render, and this is the no-observer fallback, so
      // one frame late costs nothing.
      const raf = requestAnimationFrame(() => setInView(true));
      return () => cancelAnimationFrame(raf);
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= threshold) setInView(true);
        else if (!entry.isIntersecting) setInView(false);
      },
      { threshold: [0, threshold] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, inView };
}
